import { abaDaCasca, cobreModulo, moduloDaPagina, novoDesenhoLigado, paginaComum, paginaNoSubsistema, permissaoDoSubsistema, subsistemaLigado, SUBSISTEMAS_ATIVOS, type AjusteAcesso, type ConfigSubsistemas } from '@malgoos/shared';

/**
 * Val1 (papel por sub-sistema, ficha 2026-10-28): a árvore sub-sistema > página > ação do editor de papel e da exceção por
 * pessoa. Puro (testado em `tests/arvore-acesso.test.ts`); a tela é `ArvoreAcesso.tsx`.
 *
 * O estado é o que o servidor guarda: as ações (permissões de módulo) e as páginas DESMARCADAS. A página está marcada quando o
 * módulo dela tem alguma ação marcada e ela não foi desmarcada. As ações aparecem dentro de cada página, mas são do MÓDULO:
 * marcar "Excluir" em Metas marca também em Funil (as duas são do Pipeline). Para nível diferente por página, outro papel.
 */
export interface EstadoAcesso {
  permissoes: ReadonlySet<string>;
  negadas: ReadonlySet<string>;
}

export interface PaginaArvore {
  /** `<slug>.<aba>` */
  chave: string;
  slug: string;
  aba: string;
}

export interface GrupoArvore {
  /** Código do sub-sistema, ou `comuns` (Admin, Configurações e Equipe, em todos). */
  chave: string;
  paginas: PaginaArvore[];
}

/** As páginas de cada módulo instalado (abas do web), na ordem. */
export type PaginasPorModulo = ReadonlyMap<string, readonly string[]>;

/**
 * Val5 (ações por módulo, 29/10): as chaves do rótulo de uma ação, na ordem em que o i18next tenta: a própria do módulo
 * (`usuarios.acaoModulo.<modulo>.<acao>`, ex.: "Cancelar e remarcar agendamentos" para `agenda.cancelar`) e a genérica por
 * ação (`usuarios.acaoPermissao.<acao>`). Árvore, matriz e Meus acessos usam a mesma regra.
 */
export const chavesDoRotuloDaAcao = (codigo: string, acao: string): string[] => [`usuarios.acaoModulo.${codigo}`, `usuarios.acaoPermissao.${acao}`];

/** Val8: uma página da casca nova (`<slug>.casca-<chave>` na configuração) e a página do papel que ela mostra. */
export interface PaginaCascaArvore {
  slug: string;
  chave: string;
  pagina?: string;
}

/**
 * Os grupos da árvore: um por sub-sistema, com as páginas dele na divisão vigente (a do workspace, com sub-sistemas ligados;
 * a padrão, sem eles: a mesma estrutura serve de guia), e o grupo dos módulos comuns. A página que está em dois sub-sistemas
 * aparece nos dois (é a mesma página: marcar num marca no outro). Val8: no novo desenho, a página do papel também fica no sub-sistema em que está a página da casca que a
 * mostra (o CRM do novo desenho só tem as da casca: Comercial mostra `pipeline.pipeline`, Empresas e Contatos
 * `cadastros.pessoas`...). A regra de acesso não muda: é a mesma página.
 */
export function gruposDaArvore(paginas: PaginasPorModulo, cfg: ConfigSubsistemas, casca: readonly PaginaCascaArvore[] = []): GrupoArvore[] {
  const todas: PaginaArvore[] = [...paginas.entries()].flatMap(([slug, abas]) => abas.map((aba) => ({ chave: `${slug}.${aba}`, slug, aba })));
  const comCasca = novoDesenhoLigado(cfg) ? casca.filter((c) => c.pagina) : [];
  const pelaCasca = (sub: string, chave: string) => comCasca.some((c) => c.pagina === chave && paginaNoSubsistema(cfg, sub, c.slug, abaDaCasca(c.chave)));
  // Val3: o sub-sistema desligado no workspace não aparece (sem sub-sistemas ligados, `cfg` é vazio e todos aparecem).
  const grupos: GrupoArvore[] = SUBSISTEMAS_ATIVOS.filter((sub) => subsistemaLigado(cfg, sub)).map((sub) => ({
    chave: sub,
    paginas: todas.filter((p) => !paginaComum(p.slug, p.aba) && (paginaNoSubsistema(cfg, sub, p.slug, p.aba) || pelaCasca(sub, p.chave))),
  }));
  grupos.push({ chave: 'comuns', paginas: todas.filter((p) => paginaComum(p.slug, p.aba)) });
  return grupos.filter((g) => g.paginas.length);
}

export const paginaMarcada = (e: EstadoAcesso, pagina: string) => cobreModulo(e.permissoes, moduloDaPagina(pagina)) && !e.negadas.has(pagina);

/** Ações do módulo que se podem marcar (o catálogo; quem edita só concede o que tem: `podeConceder`). */
export type AcoesPorModulo = ReadonlyMap<string, readonly string[]>;

/**
 * Marca ou desmarca uma página. Marcar sem nenhuma ação do módulo dá a de ver (`<modulo>.ver`, ou a primeira que houver);
 * desmarcar a última página marcada do módulo tira as ações dele (e limpa as desmarcações, o módulo fica de fora inteiro).
 */
export function alternarPagina(e: EstadoAcesso, pagina: string, marcar: boolean, paginas: PaginasPorModulo, acoes: AcoesPorModulo, podeConceder: (p: string) => boolean = () => true): EstadoAcesso {
  const slug = moduloDaPagina(pagina);
  const permissoes = new Set(e.permissoes);
  const negadas = new Set(e.negadas);
  const doModulo = (paginas.get(slug) ?? []).map((a) => `${slug}.${a}`);
  if (marcar) {
    negadas.delete(pagina);
    if (!cobreModulo(permissoes, slug)) {
      const lista = (acoes.get(slug) ?? []).filter(podeConceder);
      const ver = lista.find((p) => p === `${slug}.ver`) ?? lista[0];
      if (ver) permissoes.add(ver);
      // As outras páginas do módulo, que não estavam marcadas, continuam de fora.
      for (const p of doModulo) if (p !== pagina) negadas.add(p);
    }
    return { permissoes, negadas };
  }
  negadas.add(pagina);
  if (doModulo.length && doModulo.every((p) => negadas.has(p))) {
    for (const p of [...permissoes]) if (p.startsWith(`${slug}.`)) permissoes.delete(p);
    for (const p of doModulo) negadas.delete(p);
  }
  return { permissoes, negadas };
}

/** Uma ação do módulo (vale para todas as páginas dele). Desmarcar a última ação também tira as desmarcações do módulo. */
export function alternarAcao(e: EstadoAcesso, permissao: string, marcar: boolean, paginas: PaginasPorModulo): EstadoAcesso {
  const slug = permissao.slice(0, permissao.indexOf('.'));
  const permissoes = new Set(e.permissoes);
  const negadas = new Set(e.negadas);
  if (marcar) permissoes.add(permissao);
  else permissoes.delete(permissao);
  if (!cobreModulo(permissoes, slug)) for (const a of paginas.get(slug) ?? []) negadas.delete(`${slug}.${a}`);
  return { permissoes, negadas };
}

/**
 * Val3 (ficha 2026-10-29 `FUN_acoes-por-modulo-e-arvore-por-subsistema`, item 2): com sub-sistemas ligados no workspace
 * (`recorte`), o sub-sistema é o recorte de páginas do trabalho do papel. O grupo está marcado quando o papel usa o
 * sub-sistema (`subsistema.<codigo>.usar`, ou `*`); o dos módulos comuns, sempre. A REGRA DE ACESSO não muda (a página é uma
 * só): muda a apresentação, que conta em cada sub-sistema só o que foi marcado nele.
 */
export const grupoMarcado = (e: EstadoAcesso, g: GrupoArvore) => g.chave === 'comuns' || e.permissoes.has('*') || e.permissoes.has(permissaoDoSubsistema(g.chave));

/** A página como aparece no grupo: no recorte, o grupo não marcado mostra tudo desmarcado (nenhum reflexo de página compartilhada). */
export const paginaMarcadaNoGrupo = (e: EstadoAcesso, g: GrupoArvore, pagina: string, recorte = false) => (!recorte || grupoMarcado(e, g)) && paginaMarcada(e, pagina);

/** Estado do sub-sistema na árvore: todas as páginas marcadas, nenhuma, ou parte. No recorte, o não marcado é sempre "nenhuma". */
export function estadoDoGrupo(e: EstadoAcesso, g: GrupoArvore, recorte = false): 'todas' | 'nenhuma' | 'parte' {
  const n = g.paginas.filter((p) => paginaMarcadaNoGrupo(e, g, p.chave, recorte)).length;
  return n === 0 ? 'nenhuma' : n === g.paginas.length ? 'todas' : 'parte';
}

/** Os outros sub-sistemas (fora o grupo dos comuns) em que a página também aparece: o selo informativo "também em". */
export function tambemEm(grupos: readonly GrupoArvore[], g: GrupoArvore, pagina: string): string[] {
  if (g.chave === 'comuns') return [];
  return grupos.filter((x) => x.chave !== g.chave && x.chave !== 'comuns' && x.paginas.some((p) => p.chave === pagina)).map((x) => x.chave);
}

/**
 * Marca ou desmarca a página dentro de um grupo. No recorte, marcar a página num sub-sistema não marcado marca também o
 * sub-sistema (a permissão de uso, se quem edita a tem): é ali que a página passa a fazer parte do trabalho. Fora do recorte,
 * o mesmo que `alternarPagina`.
 */
export function alternarPaginaNoGrupo(
  e: EstadoAcesso,
  g: GrupoArvore,
  pagina: string,
  marcar: boolean,
  recorte: boolean,
  paginas: PaginasPorModulo,
  acoes: AcoesPorModulo,
  podeConceder: (p: string) => boolean = () => true,
): EstadoAcesso {
  let estado = e;
  if (recorte && marcar && !grupoMarcado(e, g)) {
    const usar = permissaoDoSubsistema(g.chave);
    if (!podeConceder(usar)) return e;
    estado = { permissoes: new Set([...e.permissoes, usar]), negadas: e.negadas };
  }
  return alternarPagina(estado, pagina, marcar, paginas, acoes, podeConceder);
}

/**
 * Resumo do topo ("Este papel abre: N páginas em CRM"): as páginas distintas que abrem e os sub-sistemas marcados. No
 * recorte, contam só as páginas dos grupos marcados (e as dos comuns); fora dele, todas, sem lista de sub-sistemas.
 */
export function resumoDoAcesso(e: EstadoAcesso, grupos: readonly GrupoArvore[], recorte: boolean): { paginas: number; subsistemas: string[] } {
  const abertas = new Set<string>();
  for (const g of grupos) for (const p of g.paginas) if (paginaMarcadaNoGrupo(e, g, p.chave, recorte)) abertas.add(p.chave);
  const subsistemas = recorte ? grupos.filter((g) => g.chave !== 'comuns' && grupoMarcado(e, g)).map((g) => g.chave) : [];
  return { paginas: abertas.size, subsistemas };
}

/**
 * Marcar o sub-sistema dá tudo dele: a permissão de usá-lo, todas as ações (que quem edita tem) dos módulos das páginas
 * dele e as páginas; as páginas desses módulos que ficam fora do sub-sistema (e não estavam marcadas) ficam desmarcadas.
 * Desmarcar tira a permissão de uso e desmarca as páginas dele que não estão em outro sub-sistema marcado.
 */
export function alternarGrupo(
  e: EstadoAcesso,
  g: GrupoArvore,
  marcar: boolean,
  grupos: readonly GrupoArvore[],
  paginas: PaginasPorModulo,
  acoes: AcoesPorModulo,
  podeConceder: (p: string) => boolean = () => true,
): EstadoAcesso {
  const usar = g.chave === 'comuns' ? null : permissaoDoSubsistema(g.chave);
  let estado: EstadoAcesso = e;
  if (marcar) {
    const permissoes = new Set(estado.permissoes);
    const negadas = new Set(estado.negadas);
    if (usar && podeConceder(usar)) permissoes.add(usar);
    const daqui = new Set(g.paginas.map((p) => p.chave));
    for (const slug of new Set(g.paginas.map((p) => p.slug))) {
      if (!cobreModulo(estado.permissoes, slug)) for (const a of paginas.get(slug) ?? []) if (!daqui.has(`${slug}.${a}`)) negadas.add(`${slug}.${a}`);
      for (const p of acoes.get(slug) ?? []) if (podeConceder(p)) permissoes.add(p);
    }
    for (const p of daqui) negadas.delete(p);
    return { permissoes, negadas };
  }
  const outros = grupos.filter((x) => x.chave !== g.chave && x.chave !== 'comuns' && estado.permissoes.has(permissaoDoSubsistema(x.chave)));
  const emOutro = new Set(outros.flatMap((x) => x.paginas.map((p) => p.chave)));
  if (usar) {
    const permissoes = new Set(estado.permissoes);
    permissoes.delete(usar);
    estado = { permissoes, negadas: estado.negadas };
  }
  for (const p of g.paginas) if (!emOutro.has(p.chave) && paginaMarcada(estado, p.chave)) estado = alternarPagina(estado, p.chave, false, paginas, acoes, podeConceder);
  return estado;
}

/**
 * Exceção por pessoa: a diferença entre o que a soma dos papéis dá (`base`) e o que ficou na árvore (`atual`). Ação a mais
 * concede, a menos retira; página aberta a mais concede, a menos retira.
 * Review rval1-8 (SEG-M1): a retirada de página que já estava gravada (`anteriores`) e segue valendo na árvore fica, mesmo
 * quando o papel também desmarca a página. É a retirada que veio junto com a ação concedida (a exceção é autossuficiente):
 * soltá-la ao salvar outra mudança seria conceder a página, e o servidor recusa.
 */
export function ajustesDaDiferenca(base: EstadoAcesso, atual: EstadoAcesso, todasPaginas: readonly string[], anteriores: readonly AjusteAcesso[] = []): AjusteAcesso[] {
  const saida: AjusteAcesso[] = [];
  for (const a of anteriores) {
    if (a.tipo !== 'pagina' || a.concede || !cobreModulo(atual.permissoes, moduloDaPagina(a.codigo)) || !atual.negadas.has(a.codigo)) continue;
    if (base.negadas.has(a.codigo)) saida.push(a);
  }
  for (const p of atual.permissoes) if (!base.permissoes.has(p)) saida.push({ tipo: 'permissao', codigo: p, concede: true });
  for (const p of base.permissoes) if (!atual.permissoes.has(p)) saida.push({ tipo: 'permissao', codigo: p, concede: false });
  for (const pg of todasPaginas) {
    const antes = !base.negadas.has(pg);
    const depois = !atual.negadas.has(pg);
    // Só importa onde a pessoa cobre o módulo (sem ação nenhuma, a página não abre de qualquer jeito).
    if (!cobreModulo(atual.permissoes, moduloDaPagina(pg))) continue;
    if (antes !== depois) saida.push({ tipo: 'pagina', codigo: pg, concede: depois });
  }
  return saida.sort((a, b) => a.tipo.localeCompare(b.tipo) || a.codigo.localeCompare(b.codigo));
}

/**
 * Review rval1-8 (SEG-M1): a exceção é autossuficiente. A ação concedida agora (em `atual` e não em `inicial`) leva junto a
 * retirada das páginas do módulo dela que quem edita não abre (`atorAbre`); o servidor grava o mesmo. A árvore mostra essas
 * páginas desmarcadas antes de salvar. Devolve o estado ajustado e as páginas retiradas junto.
 */
export function retirarPaginasQueOAtorNaoAbre(inicial: EstadoAcesso, atual: EstadoAcesso, paginas: PaginasPorModulo, atorAbre: (pagina: string) => boolean): { estado: EstadoAcesso; retiradas: string[] } {
  const modulos = new Set([...atual.permissoes].filter((p) => !inicial.permissoes.has(p) && p.includes('.')).map((p) => p.slice(0, p.indexOf('.'))));
  const negadas = new Set(atual.negadas);
  const retiradas: string[] = [];
  for (const slug of modulos) {
    for (const a of paginas.get(slug) ?? []) {
      const pg = `${slug}.${a}`;
      if (atorAbre(pg)) continue;
      negadas.add(pg);
      retiradas.push(pg);
    }
  }
  return retiradas.length ? { estado: { permissoes: atual.permissoes, negadas }, retiradas } : { estado: atual, retiradas };
}
